import React, { useState, useEffect } from 'react';
import { Code, Award, Heart, Briefcase, Users, Globe } from 'lucide-react';
import { createTransitionResume } from '../../../../services/resume.service';
import { formatCareerRoadmap } from '../../../../utils/resumeDataMerger';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../common/ConfirmModal';
import CreditBadge from '../common/CreditBadge';

const CareerRoadmapStep = ({ onNavigate, onDataUpdate, uploadedFile, reviewedData, targetRole, jobDescription, additionalInfo, gapAnalysis }) => {
  const [selectedActions, setSelectedActions] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

  const quickWins = [
    {
      id: 'portfolio',
      icon: <Code className="w-6 h-6" />,
      title: 'Build a Portfolio',
      description: 'Showcase your skills with real projects',
      timeframe: '2-4 weeks',
      impact: 'High',
      difficulty: 'Medium',
      detailedContent: {
        overview: 'A portfolio is your most powerful proof of ability — especially when you\'re switching careers and lack official job titles. It shows employers not just what you claim to know, but what you can actually build, design, or deliver.',
        whyItMatters: [
          'Proves your skills with tangible, verifiable evidence',
          'Demonstrates your thinking process and problem-solving',
          'Shows initiative that most applicants won\'t bother taking'
        ],
        sections: [
          {
            title: '1. Choose the Right Platform',
            items: [
              { name: 'GitHub Pages', url: 'https://pages.github.com/', description: 'Free hosting for developers', best_for: 'Developers, Data Scientists' },
              { name: 'Behance', url: 'https://www.behance.net/', description: 'Industry-standard for designers', best_for: 'Designers, Illustrators' },
              { name: 'Notion', url: 'https://www.notion.so/templates', description: 'Flexible portfolios for any field', best_for: 'Any profession' }
            ]
          }
        ],
        resources: [
          { title: 'Portfolio Building Guide', url: 'https://www.creativebloq.com/portfolios', icon: '📚' }
        ],
        actionSteps: [
          'Choose your platform based on your field',
          'Select or create 3-5 projects to feature',
          'Add your portfolio link to your resume header'
        ]
      }
    },
    {
      id: 'certification',
      icon: <Award className="w-6 h-6" />,
      title: 'Get Certified',
      description: 'Earn industry-recognized credentials',
      timeframe: '1-3 months',
      impact: 'High',
      difficulty: 'Low',
      detailedContent: {
        overview: 'Certifications close the gap between "I\'m learning" and "I\'m qualified."',
        whyItMatters: ['Signals validated, up-to-date knowledge', 'Demonstrates commitment'],
        sections: [
          {
            title: '🎓 Accredian Programs',
            items: [{ name: 'View All Programs', url: 'https://accredian.com/programs', description: 'Browse comprehensive programs' }]
          }
        ],
        resources: [{ title: 'Accredian Programs', url: 'https://accredian.com/programs', icon: '🎓' }],
        actionSteps: ['Scan job postings for required certs', 'Choose ONE to start']
      }
    },
    {
      id: 'volunteer',
      icon: <Heart className="w-6 h-6" />,
      title: 'Volunteer or Pro-Bono',
      description: 'Gain real experience while making impact',
      timeframe: '1-6 months',
      impact: 'Medium-High',
      difficulty: 'Low',
      detailedContent: {
        overview: 'Pro-bono work is one of the fastest ways to get real experience.',
        whyItMatters: ['Appears as real work experience', 'Earns you references'],
        sections: [
          { title: '🎯 Where to Find', items: [{ name: 'Catchafire', url: 'https://www.catchafire.org/', description: 'Matches skills with non-profits' }] }
        ],
        resources: [{ title: 'VolunteerMatch', url: 'https://www.volunteermatch.org/', icon: '🤝' }],
        actionSteps: ['Identify 3 causes you care about', 'Reach out to 5-8 organizations']
      }
    },
    {
      id: 'freelance',
      icon: <Briefcase className="w-6 h-6" />,
      title: 'Start Freelancing',
      description: 'Get paid while building experience',
      timeframe: '1-3 months',
      impact: 'High',
      difficulty: 'Medium',
      detailedContent: {
        overview: 'Freelancing legitimizes a career transition.',
        whyItMatters: ['Validates market demand for your skills', 'Generates real testimonials'],
        sections: [
          { title: '🌐 Platforms', items: [{ name: 'Upwork', url: 'https://www.upwork.com/', description: 'Professional projects' }] }
        ],
        resources: [{ title: 'Fiverr', url: 'https://www.fiverr.com/', icon: '🟢' }],
        actionSteps: ['Tell your network about your services', 'Set up profiles on platforms']
      }
    },
    {
      id: 'networking',
      icon: <Users className="w-6 h-6" />,
      title: 'Network in Your Field',
      description: 'Build relationships that open doors',
      timeframe: 'Ongoing',
      impact: 'Medium-High',
      difficulty: 'Low',
      detailedContent: {
        overview: 'The majority of jobs are filled through relationships.',
        whyItMatters: ['70-80% of jobs are filled via networking', 'Leads to internal referrals'],
        sections: [
          { title: '🌐 Where to Network', items: [{ name: 'LinkedIn', url: 'https://www.linkedin.com/', description: 'Professional visibility' }] }
        ],
        resources: [{ title: 'Lunchclub', url: 'https://lunchclub.com/', icon: '☕' }],
        actionSteps: ['Update your LinkedIn headline', 'Request informational interviews']
      }
    },
    {
      id: 'personal-brand',
      icon: <Globe className="w-6 h-6" />,
      title: 'Build Personal Brand',
      description: 'Get discovered before you even apply',
      timeframe: '1-3 months',
      impact: 'High',
      difficulty: 'Low',
      detailedContent: {
        overview: 'Control the narrative of your career transition.',
        whyItMatters: ['Positions you as a contributor', 'Opportunities come to you'],
        sections: [
          { title: '✍️ Content Creation', items: [{ name: 'Medium', url: 'https://medium.com/', description: 'Publish case studies' }] }
        ],
        resources: [{ title: 'Substack', url: 'https://substack.com/', icon: '📧' }],
        actionSteps: ['Share your learning journey', 'Publish one piece per week']
      }
    }
  ];

  const handleContinue = async () => {
    const generateResume = async () => {
      setProcessing(true);
      try {
        const userId = localStorage.getItem('user_id');
        const category = localStorage.getItem('category');
        const roadmapText = formatCareerRoadmap(selectedActions.map(id => quickWins.find(w => w.id === id)).filter(Boolean));
        const response = await createTransitionResume({ 
          filename: uploadedFile.filename, 
          reviewedData, 
          targetRole, 
          jobDescription, 
          additionalInfo: additionalInfo + roadmapText,
          user_id: userId,
          category: category
        });
        onNavigate('preview', { 
          result: response.result || response, 
          type: 'create', 
          targetRole, 
          reviewedData, 
          uploadedFile, 
          gapAnalysis, 
          hasCareerRoadmap: true 
        });
      } catch (error) {
        toast.error('Failed to generate resume.');
      } finally {
        setProcessing(false);
        setConfirmConfig({ isOpen: false });
      }
    };

    const atsScore = gapAnalysis?.atsScore || gapAnalysis?.matchScore || 0;
    if (atsScore < 80) {
      setConfirmConfig({
        isOpen: true,
        title: 'ATS Optimization Recommended',
        message: `Your current ATS score is ${atsScore}%. We recommend adding more experiences for better results. Do you want to continue anyway or go back to optimize?`,
        confirmLabel: 'Continue Anyway',
        cancelLabel: 'Back to Optimize',
        variant: 'info',
        onConfirm: generateResume
      });
      return;
    }

    generateResume();
  };

  const RoadmapDetailView = ({ resource, onClose }) => {
    if (!resource) return null;
    const content = resource.detailedContent;

    return (
      <div className="fixed inset-0 bg-slate-50 z-[200] flex flex-col animate-fade-in overflow-y-auto">
        <div className="bg-navy pt-12 pb-16 px-8 relative overflow-hidden">
          <div className="max-w-[1200px] mx-auto w-full relative">
            <button onClick={onClose} className="group flex items-center gap-2 text-white/70 hover:text-white transition-all mb-10 font-bold uppercase tracking-widest text-[12px]">
              <span className="msi text-[20px] group-hover:-translate-x-1 transition-transform">arrow_back</span>
              Back to Roadmap
            </button>
            <div className="flex flex-col md:flex-row md:items-center gap-8">
              <div className="w-20 h-20 rounded-3xl bg-blue-500/20 backdrop-blur-md flex items-center justify-center text-white shrink-0 shadow-2xl">
                {resource.icon}
              </div>
              <div>
                <h2 className="text-[36px] font-black font-montserrat tracking-tight text-white mb-3">{resource.title}</h2>
                <div className="flex items-center gap-4">
                  <span className="px-4 py-1.5 bg-blue-500/30 backdrop-blur-sm rounded-full text-[12px] font-bold uppercase tracking-wider text-blue-100 border border-blue-500/20">{resource.timeframe}</span>
                  <span className="px-4 py-1.5 bg-premium-green/30 backdrop-blur-sm rounded-full text-[12px] font-bold uppercase tracking-wider text-green-100 border border-premium-green/20">{resource.impact} Impact</span>
                </div>
              </div>
            </div>
            <p className="text-[18px] text-white/60 font-inter leading-relaxed max-w-4xl mt-8 font-medium italic">"{content.overview}"</p>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto w-full px-8 py-16 flex-1">
          <div className="grid lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-16">
              {content.sections.map((sec, i) => (
                <div key={i}>
                  <h3 className="text-[22px] font-black text-navy font-montserrat mb-8 flex items-center gap-4">
                    <span className="w-2 h-8 bg-blue-500 rounded-full"></span>
                    {sec.title}
                  </h3>
                  <div className="grid gap-6">
                    {sec.items.map((item, j) => (
                      <div key={j} className="p-8 bg-white border border-slate-100 rounded-[32px] group transition-all hover:shadow-2xl">
                        {item.name ? (
                          <div className="flex items-start justify-between gap-6">
                            <div className="flex-1">
                              <h4 className="text-[18px] font-bold text-navy mb-2 group-hover:text-blue-500 transition-colors">{item.name}</h4>
                              <p className="text-[15px] text-slate-500 leading-relaxed font-inter">{item.description}</p>
                            </div>
                            {item.url && (
                              <a href={item.url} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-blue-500 group-hover:text-white transition-all shrink-0">
                                <span className="msi text-[24px]">open_in_new</span>
                              </a>
                            )}
                          </div>
                        ) : (
                          <p className="text-[15px] text-slate-600 font-medium leading-relaxed">
                            {item.point && <span className="font-extrabold text-navy text-[16px] block mb-1">{item.point}</span>}
                            <span className="font-inter">{item.description}</span>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1 space-y-12">
              <div className="bg-white rounded-[40px] p-10 border border-slate-100 shadow-xl sticky top-24">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-12 h-12 rounded-2xl bg-navy text-white flex items-center justify-center">
                    <span className="msi text-[24px]">checklist</span>
                  </div>
                  <h3 className="text-[20px] font-black text-navy font-montserrat">Execution Plan</h3>
                </div>
                <div className="space-y-8">
                  {content.actionSteps.map((step, i) => (
                    <div key={i} className="flex gap-6">
                      <div className="w-10 h-10 rounded-2xl bg-blue-light text-blue-500 border border-blue-500/10 flex items-center justify-center shrink-0 font-black text-[14px]">{i + 1}</div>
                      <p className="text-[14px] text-slate-600 font-inter pt-2 leading-relaxed font-medium">{step}</p>
                    </div>
                  ))}
                </div>
                <button onClick={onClose} className="w-full mt-10 h-14 bg-blue-500 text-white font-bold rounded-xl shadow-lg">Understood, Let's go!</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-[10px] cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <div className="w-[38px] h-[38px] bg-navy rounded-[10px] flex items-center justify-center"><span className="msi text-white">article</span></div>
            <span className="font-montserrat font-extrabold text-xl text-navy tracking-[-0.4px]">Accredian AI</span>
          </div>
          <div className="flex items-center gap-6">
            <CreditBadge />
            <div className="flex items-center gap-3">
              {[
                { n: 1, l: 'Upload', s: 'green' },
                { n: 2, l: 'Review', s: 'green' },
                { n: 3, l: 'Plan', s: 'blue' },
                { n: 4, l: 'Finalize', s: 'slate' }
              ].map((step, i) => (
                <React.Fragment key={step.n}>
                  <div className={`flex items-center gap-2 text-[13px] font-semibold ${step.s === 'green' ? 'text-premium-green' : step.s === 'blue' ? 'text-blue-500' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full ${step.s === 'green' ? 'bg-premium-green text-white' : step.s === 'blue' ? 'border-2 border-blue-500 bg-blue-light text-blue-500' : 'border-2 border-slate-200 bg-slate-50 text-slate-400'} flex items-center justify-center text-[11px]`}>{step.n}</div>
                    <span className="hidden sm:inline">{step.l}</span>
                  </div>
                  {i < 3 && <div className="w-6 h-[2px] bg-slate-200" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-8 py-10 w-full flex-1">
        <div className="text-center mb-16 animate-fade-up">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-blue-500/10 text-blue mb-6">
            <span className="msi text-[36px]">travel_explore</span>
          </div>
          <h1 className="text-[40px] font-extrabold text-navy font-montserrat tracking-tight mb-4">Career Growth Roadmap</h1>
          <p className="text-[17px] text-slate-500 font-inter leading-relaxed max-w-2xl mx-auto">Fresh start? We've got you covered. Strengthen your candidacy for <strong>{targetRole}</strong>.</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 mb-12 animate-fade-up">
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm text-center sticky top-24">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-6">Current Profile Match</p>
              <div className="relative inline-flex items-center justify-center mb-8">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle cx="64" cy="64" r="58" stroke="#f1f5f9" strokeWidth="8" fill="none" />
                  <circle cx="64" cy="64" r="58" stroke="#3B82F6" strokeWidth="8" strokeDasharray={`${(gapAnalysis?.atsScore || 45) * 3.64} 364`} strokeLinecap="round" fill="none" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[28px] font-black text-navy font-montserrat">{(gapAnalysis?.atsScore || 45)}%</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">ATS Score</span>
                </div>
              </div>
              <div className="p-4 bg-blue-light/50 rounded-2xl text-[13px] text-blue-900 border border-blue-50 leading-relaxed font-medium">"Expanding your portfolio will push this score above 85%."</div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              {quickWins.map((win) => (
                <div key={win.id} className="bg-white border border-slate-200 rounded-3xl p-6 cursor-pointer hover:shadow-2xl transition-all group" onClick={() => setShowResourceModal(win)}>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-slate-50 group-hover:bg-blue-500 group-hover:text-white flex items-center justify-center transition-all">
                      {win.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[17px] font-bold text-navy font-montserrat group-hover:text-blue-500">{win.title}</h3>
                      <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{win.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-4 border-t border-slate-50">
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg text-[10px] font-bold uppercase tracking-wide">{win.timeframe}</span>
                    <span className="px-2.5 py-1 bg-blue-light text-blue rounded-lg text-[10px] font-bold uppercase tracking-wide">{win.impact} Impact</span>
                    <span className="msi ml-auto text-slate-200 group-hover:text-blue transition-colors">arrow_forward</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-12 pt-8 border-t border-slate-200 mb-20">
          <button className="h-[52px] px-8 bg-white border border-slate-200 text-navy font-bold rounded-xl" onClick={() => onNavigate('gap-analysis', { uploadedFile, reviewedData, targetRole, jobDescription, additionalInfo, gapAnalysis })}>Back</button>
          <button className="px-8 py-3 bg-blue-light/50 border-2 border-blue-500/30 text-blue font-bold rounded-xl hover:bg-blue-500 hover:text-white transition-all uppercase tracking-wider flex items-center gap-2" onClick={handleContinue}>
            <span className="msi text-[18px]">skip_next</span>
            Proceed to Preview Without Recommendations
          </button>
        </div>
      </div>

      {showResourceModal && <RoadmapDetailView resource={showResourceModal} onClose={() => setShowResourceModal(null)} />}
      <ConfirmModal
        {...confirmConfig}
        isLoading={processing}
        onCancel={() => {
          if (confirmConfig.title === 'ATS Optimization Recommended') {
            onNavigate('gap-analysis', { uploadedFile, reviewedData, targetRole, jobDescription, additionalInfo, gapAnalysis });
          }
          setConfirmConfig({ isOpen: false });
        }}
      />
    </div>
  );
};

export default CareerRoadmapStep;
